import SolutionLayout from "@/pages/solutions/components/SolutionLayout";

const features = [
  {
    title: "Private interconnects",
    desc: "Dedicated VLANs and private SBC peering for maximum security and lowest possible latency between your core and our carrier fabric.",
    icon: "ri-shield-keyhole-line",
  },
  {
    title: "Volume-based commercial terms",
    desc: "Tiered per-minute rates that improve as your traffic volume grows. No artificial caps — your rate deck auto-updates monthly based on rolling averages.",
    icon: "ri-line-chart-line",
  },
  {
    title: "N+1 SBC redundancy",
    desc: "Every interconnect uses at least two geographically separated SBCs with automatic load balancing and sub-5-second failover.",
    icon: "ri-server-line",
  },
  {
    title: "Unified CDR and billing API",
    desc: "Pull CDRs, dispute resolution data, and real-time balance via a single REST endpoint. Formats include JSON, CSV, and legacy XDR.",
    icon: "ri-file-list-line",
  },
  {
    title: "Route testing and optimization",
    desc: "Our NOC runs hourly test calls across your active destinations. When a route degrades, we proactively suggest alternatives before you notice.",
    icon: "ri-test-tube-line",
  },
  {
    title: "Regulatory guidance",
    desc: "Stay ahead of CLI validation, emergency routing, and DNC compliance requirements in the countries you serve. We monitor regulatory changes and flag them to you.",
    icon: "ri-file-list-3-line",
  },
];

const stats = [
  { value: "48h", label: "From NDA to first test call" },
  { value: "4", label: "Active SBC regions" },
  { value: "700+", label: "Interconnect partners" },
  { value: "99.97%", label: "SLA on direct links" },
];

export default function VoipOperators() {
  return (
    <SolutionLayout
      title="Solutions for VoIP Operators"
      tagline="Carrier-grade interconnects and commercial terms designed for operators who route millions of minutes per month."
      badge="Carrier grade · For VoIP Operators"
      breadcrumbLabel="For VoIP Operators"
    >
      <section className="pb-10 md:pb-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl border border-background-200 bg-background-50 p-5 text-center">
                <div className="text-2xl md:text-3xl font-semibold font-heading text-foreground-950">{s.value}</div>
                <div className="text-xs md:text-sm text-foreground-600 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-100/60 border-y border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <span className="text-xs uppercase tracking-widest text-accent-700 font-semibold">Built for operators</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-foreground-950 leading-tight">
                The interconnect experience you actually wanted.
              </h2>
              <p className="mt-4 text-foreground-700 leading-relaxed">
                Most carriers treat interconnects like commodity pipes. We treat them like partnerships. Private VLANs, proactive NOC alerts, volume-based pricing, and a billing API that actually works.
              </p>
              <div className="mt-6 rounded-xl border border-background-200 bg-background-50 p-5">
                <div className="flex items-start gap-3">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20confident%20european%20male%20network%20engineer%20in%20his%20thirties%20smiling%2C%20soft%20neutral%20studio%20background%2C%20warm%20natural%20lighting%2C%20editorial%20portrait%20photography%2C%20high%20detail&width=96&height=96&seq=vx-operator-01&orientation=squarish"
                    alt="Senior Network Engineer"
                    className="w-12 h-12 rounded-full object-cover object-top"
                  />
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">Garen H.</p>
                    <p className="text-xs text-foreground-600">Senior Network Engineer, Voxmaster</p>
                  </div>
                </div>
                <p className="mt-3 text-sm text-foreground-700 leading-relaxed">
                  &ldquo;We run inter-carrier tests every hour across 1000+ destinations. If your ASR drops on a destination, you hear from us with a replacement route before your own monitoring catches it.&rdquo;
                </p>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {features.map((f) => (
                  <div key={f.title} className="rounded-xl border border-background-200 bg-background-50 p-5 hover:border-primary-400 transition-colors">
                    <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
                      <i className={`${f.icon} text-xl w-6 h-6 flex items-center justify-center`} />
                    </span>
                    <h3 className="mt-4 text-base font-semibold text-foreground-950 font-heading">{f.title}</h3>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </SolutionLayout>
  );
}